import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Lightbulb, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import Seo, { SITE, breadcrumbSchema } from "../components/Seo";
import Markdown from "../components/common/Markdown";
import { api, formatApiError } from "../lib/api";
import { useLearning, courseStats } from "../lib/learning";
import NotFound from "./NotFound";

function Sidebar({ course, done, current, quizPassed }) {
  return (
    <aside className="h-fit rounded-3xl border border-slate-200 bg-white p-5 lg:sticky lg:top-24">
      <Link to={`/learn/${course.slug}`} data-testid="lesson-course-link" className="text-xs font-semibold uppercase tracking-wider text-gold-600 hover:underline">{course.title}</Link>
      <ol className="mt-4 space-y-1" data-testid="lesson-sidebar">
        {course.lessons.map((l, i) => (
          <li key={l.id}>
            <Link to={`/learn/${course.slug}/${l.id}`} data-testid={`sidebar-lesson-${l.id}`}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${l.id === current ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {done.includes(l.id) ? <CheckCircle2 size={16} className="shrink-0 text-mint" /> : <Circle size={16} className="shrink-0 opacity-40" />}
              <span className="flex-1">{i + 1}. {l.title}</span>
            </Link>
          </li>
        ))}
        {course.quiz_question_count > 0 && (
          <li>
            <Link to={`/learn/${course.slug}/quiz`} data-testid="sidebar-quiz"
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-600 transition-colors hover:bg-slate-50">
              {quizPassed ? <CheckCircle2 size={16} className="shrink-0 text-mint" /> : <Trophy size={16} className="shrink-0 text-gold-600" />}
              <span className="flex-1 font-semibold">Final quiz</span>
            </Link>
          </li>
        )}
      </ol>
    </aside>
  );
}

function CompleteButton({ user, done, busy, onToggle }) {
  if (!user) return <Link to="/login" data-testid="lesson-login-btn" className="rounded-full border border-navy px-6 py-3 text-sm font-semibold text-navy hover:bg-navy hover:text-white">Sign in to track progress</Link>;
  return (
    <button onClick={onToggle} disabled={busy} data-testid="lesson-complete-btn"
      className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${done ? "bg-mint/15 text-mint-600 hover:bg-mint/25" : "bg-mint text-navy hover:bg-[#19dc9b]"}`}>
      {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} {done ? "Completed · undo" : "Mark lesson complete"}
    </button>
  );
}

export default function LessonPage() {
  const { slug, lessonId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user, progress, certificates, toggle } = useLearning();
  useEffect(() => { api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setMissing(true)); }, [slug]);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [lessonId]);

  if (missing) return <NotFound />;
  if (!course) return <div className="min-h-[60vh] bg-slate-50" data-testid="lesson-loading" />;
  const idx = course.lessons.findIndex((l) => l.id === lessonId);
  if (idx < 0) return <NotFound />;
  const lesson = course.lessons[idx];
  const prev = course.lessons[idx - 1];
  const next = course.lessons[idx + 1];
  const s = courseStats(course, progress[slug]);
  const isDone = s.done.includes(lesson.id);

  const onToggle = async () => {
    setBusy(true);
    try {
      const res = await toggle(slug, lesson.id, !isDone);
      const allDone = !isDone && res.completed_lessons.length === course.lessons.length;
      if (allDone && course.quiz_question_count > 0 && !res.quiz_passed) {
        toast.success("All lessons done. Pass the final quiz to earn your certificate.");
        navigate(`/learn/${slug}/quiz`);
        return;
      }
      if (allDone) toast.success(`Course complete! You finished ${course.title}.`);
      else if (!isDone) toast.success("Lesson marked complete");
      if (!isDone && next) navigate(`/learn/${slug}/${next.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const schema = [{
    "@context": "https://schema.org", "@type": "LearningResource", name: lesson.title, url: `${SITE}/learn/${slug}/${lesson.id}`,
    educationalLevel: course.level, timeRequired: `PT${lesson.duration_minutes}M`, isAccessibleForFree: true,
    isPartOf: { "@type": "Course", name: course.title, url: `${SITE}/learn/${slug}` },
  }, breadcrumbSchema([["Home", "/"], ["Learn", "/learn"], [course.title, `/learn/${slug}`], [lesson.title, `/learn/${slug}/${lesson.id}`]])];

  return (
    <>
      <Seo title={`${lesson.title} – ${course.title}`} description={lesson.takeaways[0] || course.summary} path={`/learn/${slug}/${lesson.id}`} schema={schema} />
      <div className="sticky top-[72px] z-30 h-1 bg-slate-200"><div className="h-full bg-mint transition-[width] duration-500" style={{ width: `${s.pct}%` }} data-testid="lesson-progress-bar" /></div>
      <section className="bg-slate-100/70 py-12">
        <div className="container-q grid gap-8 lg:grid-cols-[300px_1fr]">
          <Sidebar course={course} done={s.done} current={lesson.id} quizPassed={s.quizPassed} />
          <article className="rounded-3xl border border-slate-200 bg-white p-7 sm:p-12">
            <p className="font-num text-xs text-slate-500">Lesson {idx + 1} of {course.lessons.length} · {lesson.duration_minutes} min read</p>
            <h1 className="mt-3 text-3xl font-bold text-navy sm:text-4xl" data-testid="lesson-title">{lesson.title}</h1>
            <div className="mt-8" data-testid="lesson-content"><Markdown content={lesson.content} /></div>
            {!!lesson.takeaways.length && (
              <div className="mt-10 rounded-2xl border border-gold/40 bg-gold-50 p-6" data-testid="lesson-takeaways">
                <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gold-600"><Lightbulb size={16} /> Key takeaways</p>
                <ul className="mt-4 space-y-2">{lesson.takeaways.map((t) => <li key={t} className="flex gap-3 text-sm text-slate-700"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-mint-600" />{t}</li>)}</ul>
              </div>
            )}
            {s.quizPending && (
              <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-gold/50 bg-gold-50 p-6" data-testid="quiz-pending-banner">
                <Trophy className="text-gold-600" />
                <div className="flex-1"><p className="font-semibold text-navy">All lessons done.</p><p className="text-sm text-slate-600">Pass the short final quiz to earn your certificate.</p></div>
                <Link to={`/learn/${slug}/quiz`} data-testid="lesson-take-quiz-btn" className="rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white">Take final quiz</Link>
              </div>
            )}
            {s.completed && (
              <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl bg-navy p-6 text-white" data-testid="course-complete-banner">
                <Trophy className="text-gold" />
                <div className="flex-1"><p className="font-semibold">You have completed this course.</p><p className="text-sm text-slate-400">Your Quantora certificate is ready to share on LinkedIn.</p></div>
                {certificates[slug] && <Link to={`/certificates/${certificates[slug].id}`} data-testid="lesson-view-certificate-btn" className="rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-navy">View certificate</Link>}
                <Link to="/book-consultation" data-testid="lesson-book-btn" className="rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-navy">Book free consultation</Link>
              </div>
            )}
            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-8">
              <CompleteButton user={user} done={isDone} busy={busy} onToggle={onToggle} />
              <div className="flex gap-3">
                {prev && <Link to={`/learn/${slug}/${prev.id}`} data-testid="lesson-prev-btn" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-navy hover:bg-slate-50"><ArrowLeft size={15} /> Previous</Link>}
                {next && <Link to={`/learn/${slug}/${next.id}`} data-testid="lesson-next-btn" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-navy-700">Next <ArrowRight size={15} /></Link>}
              </div>
            </div>
            <p className="mt-8 text-xs text-slate-500">Educational content only. Not investment, insurance or tax advice. Investments are subject to market risks.</p>
          </article>
        </div>
      </section>
    </>
  );
}
